import {useCallback, useEffect, useRef, useState} from "react";
import Api from "../../../../../../core/services/api/api";
import ToastService from "../../../../../../core/services/toast";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import useCachedPriceLevelsDictionaryController from "../../../../../hooks/use-price-level-dictionary";
import useIndustry from "../../../../../hooks/use-industry";
import {ImportingDataTypes} from "./types";
import {
    resolveImportIndustryId,
} from "./engine/shared/import-industry-context";
import {fetchAllFormsFromApi} from "./engine/transformers/lookup-stores/form-title-lookup-store";

const TECHNICAL_PROPERTIES_BASE_IDS = new Set([
    'partTechnicalInformation',
    'productPropertyOptions',
]);

const useImportStaticData = (selectedDataSource, formsApi, importIndustryId) => {
    const isMounted = useIsMounted();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const {allCategories, industries: allIndustries} = useIndustry({loadAllCategories: true});

    const [loading, setLoading] = useState(false);

    const [allPriceLevels, setAllPriceLevels] = useState([]);
    const [allWarehouses, setAllWarehouses] = useState([]);
    const [approvedPartners, setApprovedPartners] = useState([]);
    const [technicalPropertiesOfIndustry, setTechnicalPropertiesOfIndustry] = useState({});
    const [allForms, setAllForms] = useState([]);
    const [allProductRelationTypes, setAllProductRelationTypes] = useState([]);
    const [allPartCodeCategories, setAllPartCodeCategories] = useState([]);

    const fetchedStaticData = useRef({
        priceLevels: false,
    });

    const getAllPriceLevels = useCallback(async () => {
        if (fetchedStaticData.current.priceLevels) return;
        const [priceLevelDict, priceLevelsResponse] = await Promise.all([
            getPriceLevelDictionary(),
            Api.getAllPriceLevels()
        ]);
        if (!isMounted()) return;
        if (priceLevelsResponse?.data) {
            const dict = priceLevelDict ?? [];
            fetchedStaticData.current.priceLevels = true;
            setAllPriceLevels(priceLevelsResponse?.data?.map(e => ({
                ...e,
                title: dict?.find(d => d.priceLevelId === e.id)?.title ?? e.title
            })) ?? []);
        }
    }, [isMounted, getPriceLevelDictionary]);

    const getTechnicalPropertiesOfIndustry = useCallback(async (industryId) => {
        const propertiesResponse = await Api.getEligibleIndustryPropertiesByIndustryId(industryId);
        if (!isMounted()) return;
        if (propertiesResponse?.data) {
            setTechnicalPropertiesOfIndustry(prev => ({
                ...prev,
                [industryId]: propertiesResponse?.data ?? []
            }));
        }
    }, [isMounted]);

    const getAllWarehouses = useCallback(async () => {
        const warehousesResponse = await Api.getOperatorWarehouses();
        if (!isMounted()) return;
        if (warehousesResponse?.data) {
            setAllWarehouses(warehousesResponse?.data ?? []);
        }
    }, [isMounted]);

    const getApprovedPartners = useCallback(async () => {
        const response = await Api.partnerConnectionSellersList();
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setApprovedPartners((response.data ?? []).filter((e) => e.hasApproved));
        }
    }, [isMounted]);

    const getAllForms = useCallback(async () => {
        if (!formsApi) {
            return;
        }
        const forms = await fetchAllFormsFromApi(formsApi);
        if (!isMounted()) {
            return;
        }
        setAllForms(forms ?? []);
    }, [formsApi, isMounted]);

    const getAllProductRelationTypes = useCallback(async () => {
        const response = await Api.getAllOperatorPartRelationTypes();
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAllProductRelationTypes(response?.data ?? []);
        }
    }, [isMounted]);

    const getAllPartCodeCategories = useCallback(async () => {
        const response = await Api.listPartCodeCategories();
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAllPartCodeCategories(response?.data ?? []);
        }
    }, [isMounted]);

    const fetchStaticData = useCallback(async (sourceId, resolvedIndustryId) => {
        try {
            setLoading(true);
            switch (sourceId) {
                case ImportingDataTypes.partPriceLevels:
                    await getAllPriceLevels();
                    break;
                case ImportingDataTypes.warehouseAlertThresholds:
                case ImportingDataTypes.productWarehouseAssignment:
                case ImportingDataTypes.setInventoryAvailability:
                    await getAllWarehouses();
                    break;
                case ImportingDataTypes.productsFromAnotherPartner:
                    await Promise.all([getApprovedPartners(), getAllWarehouses()]);
                    break;
                case ImportingDataTypes.productFormAssignment:
                    await getAllForms();
                    break;
                case ImportingDataTypes.productRelationAssignment:
                    await getAllProductRelationTypes();
                    break;
                case ImportingDataTypes.productCodeAssignment:
                    await getAllPartCodeCategories();
                    break;
                default: {
                    if (TECHNICAL_PROPERTIES_BASE_IDS.has(sourceId) && resolvedIndustryId) {
                        await getTechnicalPropertiesOfIndustry(resolvedIndustryId);
                    }
                    break;
                }
            }
        } catch (error) {
            ToastService.error({
                title: 'Error',
                message: 'Error fetching required data',
                hideTicketSubmission: true
            });
        } finally {
            setLoading(false);
        }
    }, [getAllPriceLevels, getAllWarehouses, getApprovedPartners, getAllForms, getAllProductRelationTypes, getAllPartCodeCategories, getTechnicalPropertiesOfIndustry]);

    useEffect(() => {
        if (!selectedDataSource?.id) {
            return;
        }
        const resolvedIndustryId = resolveImportIndustryId(selectedDataSource.id, importIndustryId);
        void fetchStaticData(selectedDataSource.id, resolvedIndustryId);
    }, [selectedDataSource?.id, importIndustryId, fetchStaticData]);

    return {
        loading,
        allPriceLevels,
        allIndustries,
        allCategories,
        allWarehouses,
        approvedPartners,
        technicalPropertiesOfIndustry,
        allForms,
        allProductRelationTypes,
        allPartCodeCategories,
    };
};

export default useImportStaticData;
